import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { environment } from 'src/environments/environment';
import { HttpContext } from '@angular/common/http';
import { SKIP_ERROR_TOAST } from '../http-context-tokens';
import { NotificationService } from 'src/app/shared/services/notification.service';
import {
  inject,
  Component,
  OnInit,
  Input,
  Renderer2,
  OnDestroy,
  ChangeDetectorRef,
  EventEmitter,
  Output } from '@angular/core';
import { Location, LocationStrategy } from '@angular/common';
import { SelectItem } from 'primeng/api';
import { BaseServices } from 'src/app/kuba/kuba.services';
import { ActivatedRoute, Router, Params } from '@angular/router';
import {
  CommonChecklistList,
  CommonChecklistEdit,
  NodeView,
  TreeView,
  TreeData
} from 'src/app/kuba/common/models';

import { Rights, Status } from 'src/app/_models';
import { CommonChecklistService } from 'src/app/kuba/common/services/common-checklist.services';
import { ElCheckListServices } from '../../kuba/el-checklist/services/elChecklist.services';

import { FeatureKey } from 'src/app/_models';
import { HttpClient } from '@angular/common/http';
import { Observable, Subscription, catchError, forkJoin, map, of } from 'rxjs';
import {
  ChecklistModel,
  ChecklistSectionModel
} from './ecpl-checklist-viewer.model';

// Helper type to extract the inner checkList type from your main model
type InnerChecklist = ChecklistModel['content']['checkList'];
interface ChecklistTemlate {
  name: string;
  id: number;
}

@Component({
  selector: 'ecpl-checklist-viewer',
  templateUrl: './ecpl-checklist-viewer.component.html',
  styleUrls: ['./ecpl-checklist-viewer.component.css'],
  standalone: false
})
export class EcplChecklistViewerComponent implements OnInit {
  private readonly notifications = inject(NotificationService);

  @Input() id: number;
  @Input() checklistSrc: any;
  @Input() checklistName: string;
  @Input() editMode: boolean;
  @Input() isDialog: boolean;
  @Input() forceReopen: number;

  @Output() saveChanges: EventEmitter<any> = new EventEmitter<any>();
  showModalWindow: boolean;
  saving = false;
  providerName = '';
  showCopy = false;
  copyName = '';
  hideOriginal = false;
  copied = false;

  get canCopy(): boolean {
    return !this.canEdit && BaseServices.BusinessId > 0 && (this.featureType === 'Manuals' || this.featureType === 'AssignManuals');
  }

  loadProvider(): void {
    if (!this.canCopy || !this.checkListData?.checkList?.id) return;
    this.subscriptions.push(this.http.get(`${environment.BASE_URL_V2}/checklists/manual/${this.checkListData.checkList.id}/provider`,
      { responseType: 'text' }).subscribe(name => { this.providerName = name.replace(/^"|"$/g, ''); }));
  }

  openCopy(): void {
    this.copyName = this.checkListData.checkList.title;
    this.showCopy = true;
  }

  copyToBusiness(): void {
    if (!this.copyName.trim() || this.saving) return;
    this.saving = true;
    this.saveError = false;
    this.subscriptions.push(this.http.post<any>(`${environment.BASE_URL_V2}/checklists/manual/${this.checkListData.checkList.id}/copy?hideOriginal=${this.hideOriginal}`,
      { Title: this.copyName },
      { context: new HttpContext().set(SKIP_ERROR_TOAST, true) }).subscribe({
      next: result => {
        this.checkListData = { checkList: {
          id: result.Id, title: result.Title, scoreIndex: result.ScoreIndex, isEditable: true,
          sections: (result.Sections ?? []).map(section => ({
            id: section.Id, title: section.Title, position: section.Position, status: section.Status,
            items: (section.Items ?? []).map(item => ({ id: item.Id, title: item.Title, position: item.Position,
              status: item.Status, referenceImagePath: item.ReferenceImagePath }))
          }))
        }};
        this.cleanSectionTags(this.checkListData.checkList.sections);
        this.copied = true;
        this.editMode = true;
        this.showCopy = false;
        this.saving = false;
      },
      error: () => { this.saving = false; this.saveError = true; }
    }));
  }

  saveError = false;
  imageError = false;
  imageBusy = new Set<object>();
  pendingImages = new Map<object, { file: File; preview: string | null }>();
  previewImage: string | null = null;

  get canQueueImages(): boolean {
    return this.featureType === 'Manuals' || this.featureType === 'AssignManuals';
  }

  get canEdit(): boolean {
    return this.editMode && this.checkListData?.checkList?.isEditable === true;
  }

  get checkpointCount(): number {
    return (this.checkListData?.checkList?.sections ?? []).filter(s => +s.status !== 0)
      .reduce((count, s) => count + (s.items ?? []).filter(i => +i.status !== 0).length, 0);
  }

  reorder(event: CdkDragDrop<any[]>, items: any[]): void {
    if (!this.canEdit || this.saving) return;
    moveItemInArray(items, event.previousIndex, event.currentIndex);
    this.positioning();
  }

  move(items: any[], index: number, offset: number): void {
    if (!this.canEdit || index + offset < 0 || index + offset >= items.length) return;
    moveItemInArray(items, index, index + offset);
    this.positioning();
  }

  /** Scrolls the outline's target section into view without touching the router. */
  scrollToSection(index: number): void {
    document
      .getElementById(`checklist-section-${this.id}-${index}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  imageUrl(item: any): string {
    return `${environment.BASE_URL_V2}/checklists/${this.featureType === 'Manuals' || this.featureType === 'AssignManuals' ? 'manual' : JSON.parse(sessionStorage.getItem('workingModule') || '{}').id === FeatureKey.ELECTRO ? 'electro' : 'feature'}/${this.checkListData.checkList.id}/items/${item.id}/reference-image`;
  }

  uploadImage(event: Event, item: any): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !this.canEdit || (!item.id && !this.canQueueImages)) return;
    if (file.size > 5 * 1024 * 1024 || !/\.(png|jpe?g|webp|heic)$/i.test(file.name)) {
      this.imageError = true;
      return;
    }
    this.imageError = false;
    if (!item.id) {
      this.discardPendingImage(item);
      this.pendingImages.set(item, { file, preview: /\.heic$/i.test(file.name) ? null : URL.createObjectURL(file) });
      return;
    }
    this.subscriptions.push(this.postImage(item, file).subscribe({ error: () => { this.imageError = true; } }));
  }

  private postImage(item: any, file: File): Observable<void> {
    this.imageBusy.add(item);
    const form = new FormData();
    form.append('image', file);
    return this.http.post<{ ReferenceImagePath: string }>(this.imageUrl(item), form,
      { context: new HttpContext().set(SKIP_ERROR_TOAST, true) }).pipe(
      map(result => {
        item.referenceImagePath = result.ReferenceImagePath;
        this.discardPendingImage(item);
        this.imageBusy.delete(item);
      }),
      catchError(error => { this.imageBusy.delete(item); throw error; })
    );
  }

  private discardPendingImage(item: object): void {
    const pending = this.pendingImages.get(item);
    if (pending?.preview) URL.revokeObjectURL(pending.preview);
    this.pendingImages.delete(item);
  }

  private applySavedIds(saved: any): void {
    const sections = this.checkListData?.checkList?.sections ?? [];
    (saved?.parentItems ?? []).forEach((savedSection: any, s: number) => {
      if (!sections[s]) return;
      sections[s].id = savedSection.parentItemId;
      (savedSection.childItems ?? []).forEach((savedItem: any, i: number) => {
        if (sections[s].items?.[i]) sections[s].items[i].id = savedItem.childItemId;
      });
    });
  }

  private uploadPendingImages(): void {
    const sections = this.checkListData?.checkList?.sections ?? [];
    const queued = [...this.pendingImages].filter(([item]: [any, unknown]) =>
      item.id && +item.status !== Status.Deleted && sections.some((s: any) => +s.status !== Status.Deleted && s.items?.includes(item)));
    if (queued.length === 0) {
      this.hideModal();
      return;
    }
    this.subscriptions.push(forkJoin(queued.map(([item, pending]) =>
      this.postImage(item, pending.file).pipe(map(() => true), catchError(() => of(false))))).subscribe(results => {
      if (results.every(Boolean)) this.hideModal();
      else this.imageError = true;
    }));
  }

  removeImage(item: any): void {
    if (this.pendingImages.has(item)) {
      this.discardPendingImage(item);
      return;
    }
    if (!this.canEdit || !item.id) return;
    this.imageBusy.add(item);
    this.subscriptions.push(this.http.delete(this.imageUrl(item),
      { context: new HttpContext().set(SKIP_ERROR_TOAST, true) }).subscribe({
      next: () => { item.referenceImagePath = null; this.imageBusy.delete(item); },
      error: () => { this.imageError = true; this.imageBusy.delete(item); }
    }));
  }

  checkListData: any;
  checkListEvent: any;
  checklistTemlates: ChecklistTemlate[];
  selectedChecklistTemplate: ChecklistTemlate;
  createdOn: any;
  featureType: string;
  isChecklistRights = true;
  checklistTemplateList: SelectItem[];
  copyChecklistId: number;
  userRole: string;
  pathValue: string;
  showCompleteChecklist: boolean;
  title: string;
  private subscriptions: Subscription[] = [];

  constructor(
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef,
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    private commonChecklistService: CommonChecklistService,
    private elCheckListServices: ElCheckListServices,
    location: LocationStrategy
  ) {
    this.pathValue = route.snapshot.url[0].path;
    location.onPopState(() => {
      this.showModalWindow = false;
      this.renderer.removeClass(document.body, 'modal-open');
    });
  }

  /**
   * Checks if the sections data is already in hierarchical format
   * (i.e., sections have items arrays with actual question data)
   */
  isAlreadyHierarchical(sections: any[]): boolean {
    // If any section has items with actual content, it's hierarchical
    return sections.some(
      section =>
        Array.isArray(section.items) &&
        section.items.length > 0 &&
        section.items.some(item => item.title && item.title.trim() !== '')
    );
  }

  /**
   * Removes _#SEC# or #SEC# tags from section titles without restructuring.
   * Handles both 'title' and 'Title' property naming variations.
   */
  cleanSectionTags(sections: any[]) {
    if (!Array.isArray(sections)) return;
    sections.forEach(section => {
      const titleProp = section.title !== undefined ? 'title' :section.Title !== undefined ? 'Title' : null;
      if (titleProp && section[titleProp] && typeof section[titleProp] === 'string') {
        section[titleProp] = section[titleProp].replace('_#SEC#', '').replace('#SEC#', '');
      }
    });
  }

  showModal() {
    this.checkListData = null;

    // 1. Determine the source of the data
    let rawData = this.checklistSrc.content
      ? JSON.parse(JSON.stringify(this.checklistSrc.content))
      : JSON.parse(JSON.stringify(this.checklistSrc));

    // 2. Locate the sections array
    const sectionsContainer = rawData.checkList || rawData.checklist;

    if (
      sectionsContainer &&
      Array.isArray(sectionsContainer.sections) &&
      sectionsContainer.sections.length > 0
    ) {
      // 3. Check if data needs hierarchy fix
      const needsHierarchyFix = !this.isAlreadyHierarchical(
        sectionsContainer.sections
      );

      if (needsHierarchyFix) {
        // FORCE THE HIERARCHY FIX for flat data
        // We run this if we see sections but they are flattened (siblings)
        const fixedSections = this.mapFlatListToHierarchy(
          sectionsContainer.sections
        );

        // 4. Update the data
        sectionsContainer.sections = fixedSections;
      } else {
        // Data is already hierarchical, just clean the tags
        this.cleanSectionTags(sectionsContainer.sections);
      }
    } else {
      // Handle completely empty/new scenario
      if (sectionsContainer && !sectionsContainer.sections) {
        sectionsContainer.sections = [];
        // Create default empty section
        sectionsContainer.sections.push({
          id: 0,
          position: 1,
          title: '',
          status: 1,
          items: [{ id: 0, position: 1, title: '', status: 1 }]
        });
      }
    }

    // 5. Assign to the component variable
    this.checkListData = rawData;
    this.loadProvider();

    let workingModule = JSON.parse(sessionStorage.getItem('workingModule')!);
    const isUnderCompletion =
      this.checklistSrc.latestCompletionStatus == 'UNDERCOMPLETION';
    const isModuleIdMatch =
      workingModule.id == FeatureKey.FOOD_SAFETY ||
      workingModule.id == FeatureKey.KUBA_CONTROL;

    if (isModuleIdMatch && isUnderCompletion) {
      this.completeChecklist();
    } else {
      this.showModalWindow = true;
      this.renderer.addClass(document.body, 'modal-open');
      this.initialBind();
      this.copyChecklistId = null!;
    }

    setTimeout(() => this.positioning(), 100);
  }

  /**
   * Converts a flat list of API objects into a Section -> Items hierarchy.
   * Assumes the FIRST item is the Section, and the rest are children.
   */
  mapFlatListToHierarchy(flatList: any[]): any[] {
    // Hierarchical responses already contain sections, including empty ones.
    // Only reconstruct genuinely flat records that expose their parent IDs.
    if (!flatList.some(item => item.parentItemId !== undefined || item.ParentItemId !== undefined)) {
      this.cleanSectionTags(flatList);
      return flatList;
    }
    const sections = flatList.filter(item => +(item.parentItemId ?? item.ParentItemId ?? 0) === 0);
    for (const section of sections) {
      section.items = flatList.filter(item => +(item.parentItemId ?? item.ParentItemId) === +(section.id ?? section.Id))
        .sort((a, b) => +(a.position ?? a.Position) - +(b.position ?? b.Position));
    }
    this.cleanSectionTags(sections);
    return sections.sort((a, b) => +(a.position ?? a.Position) - +(b.position ?? b.Position));
  }

  updateItemStatus(event: any, item: any) {
    if (!this.canEdit) return;
    if (event.currentTarget.checked) {
      item.status = 1;
    } else {
      item.status = 2;
    }
  }

  /**
   * Adds a new Section to the provided checklist.
   * Initializes the section with default values and a single empty child item.
   * Triggers a positioning recalculation after adding.
   *
   * @param checklist - The checklist data object containing the sections array.
   */
  addNewSection(checklist: InnerChecklist) {
    if (!checklist.sections) {
      checklist.sections = [];
    }

    // Create the new Section
    const newSection: ChecklistSectionModel = {
      id: 0,
      position: checklist.sections.length + 1,
      title: '',
      status: '1',
      items: [
        {
          id: 0,
          position: 1,
          title: '',
          status: '1'
        }
      ]
    };

    checklist.sections.push(newSection);
    this.positioning();
  }

  updateScoreIndex(event: any) {
    if (!this.canEdit) return;
    this.checkListData.checkList.scoreIndex = event.currentTarget.checked;
  }

  /**
   * Adds a new Item (Checkpoint/Question) to a specific Section.
   * Initializes the item with default values and appends it to the section's item list.
   * Triggers a positioning recalculation after adding.
   *
   * @param sec - The specific Section model where the item should be added.
   * @param index - The index of the section (used for UI tracking).
   */
  addItem(sec: ChecklistSectionModel, index: number) {
    if (!Array.isArray(sec.items)) {
      sec.items = [];
    }

    sec.items.push({
      id: 0,
      position: 0,
      title: '',
      status: '1'
    });

    this.positioning();
  }

  removeItem(sec: any, item: any) {
    item.status = 0;
    this.positioning();
  }

  removesection(checklist: any, section: any) {
    section.status = 0;
    this.positioning();
  }

  hideModal() {
    if (this.saving || this.imageBusy.size > 0) return;
    [...this.pendingImages.keys()].forEach(item => this.discardPendingImage(item));
    this.showModalWindow = false;
    this.renderer.removeClass(document.body, 'modal-open');
    if (this.copied) { this.copied = false; this.saveChanges.emit({ eventName: 'ChecklistCopied' }); }
  }

  copyManualCheckList() {
    if (this.copyChecklistId) {
      this.subscriptions.push(
        this.commonChecklistService
          .getChecklist(
            this.route.snapshot.params['mid'],
            this.copyChecklistId,
            'MANUAL'
          )
          .subscribe((result: any) => {
            if (result) {
              const copiedChecklist = {
                title: result.Title,
                scoreIndex: result.ScoreIndex,
                status: result.Status,
                sections: result.Items.map((section: any) => ({
                  title: section.Title,
                  position: section.Position,
                  id: section.Id,
                  status: section.Status,
                  items: section.SubItems.map((item: any) => ({
                    title: item.Title,
                    id: item.Id,
                    position: item.Position,
                    status: item.Status
                  }))
                }))
              };

              // Copy sections and items from the selected template
              if (
                copiedChecklist.sections &&
                Array.isArray(copiedChecklist.sections)
              ) {
                this.checkListData.checkList.sections = JSON.parse(
                  JSON.stringify(copiedChecklist.sections)
                );

                // Clean the section tags
                this.cleanSectionTags(this.checkListData.checkList.sections);

                // Reset IDs for new checklist
                this.checkListData.checkList.sections.forEach(
                  (section: any) => {
                    section.id = 0;
                    if (section.items && Array.isArray(section.items)) {
                      section.items.forEach((item: any) => {
                        item.id = 0;
                      });
                    }
                  }
                );

                this.positioning();
                this.cdr.detectChanges();
              }
            }
          })
      );
    }
  }

  completeChecklist() {
    let workingModule = JSON.parse(sessionStorage.getItem('workingModule')!);
    let featureName = workingModule.name;
    if (featureName !== 'Manuals') {
      let id = this.route.snapshot.params['Id'];
      this.hideModal();
      if (this.pathValue == 'checklist') {
        this.router.navigate(
          [
            '../../../../complete-checklist/new/' +
              this.checkListData.checkList.id,
            'feature',
            workingModule.id,
            this.route.snapshot.params['Id'],
            'null'
          ],
          { relativeTo: this.route }
        );
      } else if (this.pathValue == 'dailytask') {
        if (this.checklistSrc.isUnderCompleteChecklist) {
          this.router.navigate(
            [
              '../../../../complete-checklist/incomplete/' +
                this.checklistSrc.underCompleteChecklistId,
              'feature',
              workingModule.id,
              this.route.snapshot.params['Id']
            ],
            { relativeTo: this.route }
          );
        } else {
          this.router.navigate(
            [
              '../../../../complete-checklist/new/' +
                this.checkListData.checkList.id,
              'feature',
              workingModule.id,
              this.route.snapshot.params['Id'],
              0
            ],
            { relativeTo: this.route }
          );
        }
      } else {
        this.router.navigate(
          [
            '../../../../../complete-checklist/new/' +
              this.checkListData.checkList.id,
            'feature',
            workingModule.id,
            this.route.snapshot.params['Id'],
            0
          ],
          { relativeTo: this.route }
        );
      }
    }
  }

  informationHide() {
    this.showCompleteChecklist = false;
  }

  saveChecklist() {
    if (!this.canEdit || this.saving || this.validation()) return;
    const sections = this.checkListData?.checkList?.sections ?? [];
    this.checkListData.checkList.sections = Array.isArray(sections)
      ? sections
      : [];

    this.checkListData.checkList.sections.forEach((s: any) => {
      s.items = Array.isArray(s?.items) ? s.items : [];
    });


    if ((this.checkListData?.checkList?.title ?? '').trim() === '') {
      return false as any;
    }

    const seen = new Set<string>();
    for (const s of this.checkListData.checkList.sections) {
      const key = (s?.title ?? '').trim().toLowerCase();
      if (key && seen.has(key)) {
        this.alertMsg?.();
        return;
      }
      seen.add(key);
    }

    this.positioning();
    let dataToSend = JSON.parse(JSON.stringify(this.checkListData));

    if (dataToSend.checkList && Array.isArray(dataToSend.checkList.sections)) {
      dataToSend.checkList.sections.forEach((sec: any) => {
        // We add the tag ONLY to the cloned data going to the database
        if (sec.title && !sec.title.includes('_#SEC#')) {
          sec.title = sec.title + '_#SEC#';
        }
      });
    }

    const isNew = (this.checkListData?.checkList?.id ?? 0) === 0;

    // Emit the Cloned+Tagged data
    this.checkListEvent = {
      eventName: isNew ? 'NewChecklist' : 'SaveChecklist',
      item: dataToSend,
      onSaved: (success: boolean, saved?: any) => {
        this.checkListData.checkList.id = dataToSend.checkList.id;
        this.saving = false;
        this.saveError = !success;
        if (!success) return;
        this.applySavedIds(saved);
        this.uploadPendingImages();
      }
    };

    this.saving = true;
    this.saveError = false;
    this.saveChanges.emit(this.checkListEvent);
  }

  ngOnInit() {
    this.userRole = BaseServices.UserRole;
    this.checkListData = null;
    this.checkListData = this.checklistSrc.content;
    let workingModule = JSON.parse(sessionStorage.getItem('workingModule')!);
    this.featureType = workingModule.name;
    this.onChecklistRights();
  }

  /**
   * Updates the positions of sections and their items in the checklist data.
   * This method ensures that sections and items are assigned sequential positions
   * based on their current order and status. Only sections and items with a non-zero
   * status are considered for positioning.
   */
  positioning(): void {
    const sections = this.checkListData?.checkList?.sections ?? [];
    if (!Array.isArray(sections)) return;

    let secPos = 1;

    sections.forEach((section: any) => {
      if ((section?.status ?? 0) !== 0) {
        const items = Array.isArray(section?.items) ? section.items : [];
        section.items = items;

        section.position = secPos;

        let itemPos = 1;
        items.forEach((item: any) => {
          if ((item?.status ?? 0) !== 0) {
            item.position = itemPos++;
          }
        });

        secPos++;
      }
    });
  }

  alertMsg() {
    this.notifications.warn('UI_NOTIFICATIONS.SECTION_DUPLICATE');
  }

  validation() {
    let status = false;
    if (this.checkListData.checkList.title.trim() === '') {
      status = true;
    }
    if (Array.isArray(this.checkListData?.checkList?.sections)) {
      this.checkListData.checkList.sections.forEach((section: any) => {
        if (section?.title?.trim() === '' && section?.status !== 0) {
          status = true;
        }
        if (section?.status !== 0 && Array.isArray(section.items)) {
          section.items.forEach((item: any) => {
            if (item?.title?.trim() === '' && item?.status !== 0) {
              status = true;
            }
          });
        }
      });
    }
    return status;
  }

  initialBind() {
    this.getCheckListTemplateList(
      BaseServices.ApplicationId,
      +BaseServices.PortalId,
      BaseServices.BusinessId,
      +BaseServices.roleId
    );
  }

  getCheckListTemplateList(
    appId: number,
    portalId: number,
    BusinessId: number,
    roleId: number
  ) {
    this.checklistTemplateList = [];
    let appManualId = this.route.snapshot.params['mid'];
    if (roleId === 1) {
      portalId = 0;
      BusinessId = 0;
    } else if (roleId == 2) {
      BusinessId = 0;
    } else {
      portalId = 0;
    }
    this.subscriptions.push(
      this.commonChecklistService
        .getCheckListforTemplate(
          appManualId,
          appId,
          portalId,
          BusinessId,
          roleId
        )
        .subscribe((result: any) => {
          if (result) {
            this.checklistTemplateList = [];
            result.forEach((element: any) => {
              this.checklistTemplateList.push({
                label: element.Title + ' (' + element.manualTitle + ')',
                value: element.Id
              });
            });
          }
        })
    );
  }

  onChecklistRights() {
    let currentUserRole = BaseServices.UserRole;
    let userRightsId = Rights.CHECKLIST_RIGHTS;
    if (currentUserRole === 'Guest') {
      this.isChecklistRights = BaseServices.checkUserRights(
        userRightsId,
        currentUserRole
      );
    }
    let workingModule = JSON.parse(sessionStorage.getItem('workingModule')!);
    if (workingModule.id === FeatureKey.ELECTRO) {
      this.isChecklistRights = false;
    }
  }

  validateCopyCheckList() {
    if (this.copyChecklistId === undefined || this.copyChecklistId === null) {
      return true;
    } else {
      return false;
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub, i) => {
      sub.unsubscribe();
    });
  }
}
